import {
  configureStore,
  combineReducers,
  GetThunkAPI,
  ThunkDispatch,
} from '@reduxjs/toolkit';
import { UnknownAction } from 'redux';
import tokenMiddleware from './tokenMiddleware';
import notificationSlice from './notifications/NotificationsSlice';
import progressSlice from './progress/ProgressSlice';
import SessionSlice from './session/SessionSlice';
import SiteGuideSlice from './siteGuide/SiteGuideSlice';
import loginModalSlice from './loginModal/LoginModalSlice';
import pdpSlice from './pdp/pdpSlice';
import makhsanRolesSlice from './makhsanRoles/makhsanRolesSlice';
import categoriesSlice from './categories/categoriesSlice';
import makhsanSystemConfigSlice from './makhsanSystemConfig/makhsanSystemConfigSlice';
import makhsanUserInfoSlice from './makhsanUserInfo/makhsanUserInfoSlice';
import providerSlice from './providers/providerSlice';
import companiesSlice from './companies/companiesSlice';
import usersSlice from './users/usersSlice';
import appInstanceSlice from './appInstance/appInstanceSlice';
import tokensSlice from './tokens/tokensSlice';
import templatesSlice from './templates/templatesSlice';
import tasksSlice from './tasks/tasksSlice';
import thisUserSlice from './thisUser/thisUserSlice';
import limitationsSlice from './limitations/limitationsSlice';
import messagesSlice from './messages/messagesSlice';
import conversationSlice from './conversation/conversationSlice';
import surveySlice from './survey/surveySlice';
import appSlice from './app/appSlice';

const appReducer = combineReducers({
  app: appSlice,
  appInstance: appInstanceSlice,
  categories: categoriesSlice,
  companies: companiesSlice,
  conversations: conversationSlice,
  limitations: limitationsSlice,
  loginModal: loginModalSlice,
  makhsanRoles: makhsanRolesSlice,
  makhsanSystemConfig: makhsanSystemConfigSlice,
  makhsanUserInfo: makhsanUserInfoSlice,
  messages: messagesSlice,
  notifications: notificationSlice,
  pdp: pdpSlice,
  progress: progressSlice,
  providers: providerSlice,
  session: SessionSlice,
  siteGuide: SiteGuideSlice,
  survey: surveySlice,
  tasks: tasksSlice,
  templates: templatesSlice,
  thisUser: thisUserSlice,
  tokens: tokensSlice,
  users: usersSlice,
});

const rootReducer = (state: any, action: any) => {
  if (action.type === 'thisUser/logoutUser') {
    state = undefined;
  }

  return appReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
      thunk: {
        extraArgument: { tokenMiddleware },
      },
    });
  },
});

export default store;

export type RootState = ReturnType<typeof store.getState>;

type AsyncThunkConfig = {
  state?: unknown;
  dispatch?: ThunkDispatch<unknown, unknown, UnknownAction>;
  extra?: unknown;
  rejectValue?: unknown;
  serializedErrorType?: unknown;
  pendingMeta?: unknown;
  fulfilledMeta?: unknown;
  rejectedMeta?: unknown;
};

export type IApp = RootState['app'];
export type IAppInstance = RootState['appInstance'];
export type ICategories = RootState['categories'];
export type ICompanies = RootState['companies'];
export type IConversations = RootState['conversations'];
export type ILimitations = RootState['limitations'];
export type ILoginModal = RootState['loginModal'];
export type IMakhsanRoles = RootState['makhsanRoles'];
export type IMakhsanSystemConfig = RootState['makhsanSystemConfig'];
export type IMakhsanUserInfo = RootState['makhsanUserInfo'];
export type IMessages = RootState['messages'];
export type INotifications = RootState['notifications'];
export type IPdp = RootState['pdp'];
export type IProgress = RootState['progress'];
export type IProviders = RootState['providers'];
export type ISession = RootState['session'];
export type ISiteGuide = RootState['siteGuide'];
export type ISurvey = RootState['survey'];
export type ITasks = RootState['tasks'];
export type ITemplates = RootState['templates'];
export type IThisUser = RootState['thisUser'];
export type ITokens = RootState['tokens'];
export type IUsers = RootState['users'];
export type TThunkApi = GetThunkAPI<AsyncThunkConfig>;

export const appDispatch = (action: any) => {
  let fn = store.dispatch(action);

  if ('unwrap' in fn) {
    fn = fn.unwrap();
  }

  return fn;
};
